/**
 * The files of a project's checkout, on the host that holds it.
 *
 * Not a file manager in the sense the phrase usually means — there is no drive to
 * mount and no second login. A repository already records which host holds which
 * absolute path, so the root is a first-class object rather than a directory
 * somebody typed, and everything below it is reached over the same SSH transport
 * the terminals use.
 *
 * Three things the interface has to say plainly, because each is a fact an
 * operator would otherwise have to guess at:
 *
 *   - **A symlink leading out of the root is shown and marked.** Hiding it would
 *     leave somebody wondering where a name went; following it is refused by the
 *     server, and the mark is what explains the refusal before it happens.
 *   - **A file too large to read says so** rather than appearing empty.
 *   - **Binary is decided on the bytes, not the name**, so a .txt full of NULs is
 *     reported as binary and not rendered as a page of replacement characters.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, File, Folder, FolderPlus, Link2, Home, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { Icon } from '@/components/icon'
import { Button, EmptyState } from '@/components/primitives'
import { Notice, QueryState } from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { useT } from '@/i18n/context'
import { formatBytes } from '@/i18n'
import type { Project, Repository } from '@/types/domain'

interface FileEntry {
  name: string
  dir: boolean
  symlink: boolean
  size: number
  mode: string
  modTime: string
  outside?: boolean
}

interface FileListing {
  path: string
  entries: FileEntry[]
  truncated?: boolean
}

interface FileContent {
  path: string
  name: string
  size: number
  mode: string
  content?: string
  binary: boolean
}

export function FilesTab({ project }: { project: Project }) {
  const t = useT()
  const repos = project.repositories ?? []
  const [repoId, setRepoId] = useState<string>(() => repos[0]?.id ?? '')
  const repo = repos.find((r) => r.id === repoId)
  const [path, setPath] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  if (repos.length === 0) {
    return <EmptyState title={t('files.noRepos')} hint={t('files.noReposHint')} />
  }

  return (
    <div className="k-files">
      {repos.length > 1 && (
        <div className="k-files__repos" role="tablist" aria-label={t('files.repository')}>
          {repos.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={r.id === repoId}
              className="k-files__repo"
              onClick={() => {
                setRepoId(r.id)
                // A path is relative to *its* repository; carrying it across
                // would ask one checkout for another's directory.
                setPath('')
                setOpen(null)
              }}
            >
              {r.name}
            </button>
          ))}
        </div>
      )}

      {repo && (
        <>
          <Crumbs root={repo.name} path={path} onGo={(p) => { setPath(p); setOpen(null) }} />
          <Listing
            projectId={project.id}
            repo={repo}
            path={path}
            onEnter={(p) => { setPath(p); setOpen(null) }}
            onOpen={setOpen}
          />
          {open !== null && (
            <Viewer projectId={project.id} repo={repo} path={open} onClose={() => setOpen(null)} />
          )}
        </>
      )}
    </div>
  )
}

/** Where you are, and every step back to the root. */
function Crumbs({
  root,
  path,
  onGo,
}: {
  root: string
  path: string
  onGo: (path: string) => void
}) {
  const parts = path === '' ? [] : path.split('/')
  return (
    <nav className="k-files__crumbs" aria-label={root}>
      <button type="button" className="k-files__crumb" onClick={() => onGo('')}>
        <Icon icon={Home} size={13} />
        <span>{root}</span>
      </button>
      {parts.map((part, i) => (
        <span key={`${part}-${i}`} className="k-files__crumbwrap">
          <Icon icon={ChevronRight} size={12} />
          <button
            type="button"
            className="k-files__crumb"
            onClick={() => onGo(parts.slice(0, i + 1).join('/'))}
          >
            {part}
          </button>
        </span>
      ))}
    </nav>
  )
}

function Listing({
  projectId,
  repo,
  path,
  onEnter,
  onOpen,
}: {
  projectId: string
  repo: Repository
  path: string
  onEnter: (path: string) => void
  onOpen: (path: string) => void
}) {
  const t = useT()
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const [making, setMaking] = useState(false)
  const [name, setName] = useState('')

  const op = useMutation({
    mutationFn: (body: { op: string; path: string; to?: string }) =>
      api.post<void>(`/repositories/${repo.id}/files/op?projectId=${projectId}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['repo-files', repo.id] }),
  })

  const q = useQuery({
    queryKey: ['repo-files', repo.id, path],
    queryFn: () =>
      api.get<FileListing>(`/repositories/${repo.id}/files`, { projectId, path }),
  })

  const join = (name: string) => (path === '' ? name : `${path}/${name}`)

  return (
    <QueryState
      pending={q.isPending}
      error={q.error}
      onRetry={() => void q.refetch()}
      empty={(q.data?.entries.length ?? 0) === 0}
      emptyTitle={t('files.empty')}
      emptyHint={t('files.emptyHint')}
    >
      {q.data?.truncated && <Notice tone="warn">{t('files.truncated')}</Notice>}
      {op.error && <Notice tone="err">{String(op.error)}</Notice>}

      <div className="k-files__ops">
        {making ? (
          <form
            className="k-files__mkdir"
            onSubmit={(e) => {
              e.preventDefault()
              if (!name.trim()) return
              op.mutate({ op: 'mkdir', path: path === '' ? name.trim() : `${path}/${name.trim()}` })
              setName('')
              setMaking(false)
            }}
          >
            <input
              autoFocus
              className="k-files__mkdirinput"
              value={name}
              placeholder={t('files.newFolderName')}
              aria-label={t('files.newFolderName')}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setMaking(false)
                  setName('')
                }
              }}
            />
            <Button size="sm" type="submit">{t('action.create')}</Button>
          </form>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            icon={<Icon icon={FolderPlus} size={14} />}
            onClick={() => setMaking(true)}
          >
            {t('files.newFolder')}
          </Button>
        )}
      </div>
      <ul className="k-files__list">
        {[...(q.data?.entries ?? [])]
          // Directories first, then by name — the order every file browser uses,
          // and the one an operator scanning for a folder expects.
          .sort((a, b) => Number(b.dir) - Number(a.dir) || a.name.localeCompare(b.name, 'tr'))
          .map((e) => (
            <li key={e.name} className="k-files__row">
              <button
                type="button"
                className="k-files__entry"
                disabled={e.outside}
                onClick={() => (e.dir ? onEnter(join(e.name)) : onOpen(join(e.name)))}
              >
                <Icon icon={e.symlink ? Link2 : e.dir ? Folder : File} size={14} />
                <span className="k-files__name">{e.name}</span>
                {!e.dir && <span className="k-files__size k-mono">{formatBytes(e.size)}</span>}
                <span className="k-files__mode k-mono">{e.mode}</span>
              </button>
              {e.outside && (
                /* Shown rather than hidden: a name that simply vanished would be
                   a worse answer than one that says where it leads. */
                <span className="k-files__outside">{t('files.outside')}</span>
              )}
              <button
                type="button"
                className="k-files__remove"
                aria-label={t('files.remove', { name: e.name })}
                title={t('files.remove', { name: e.name })}
                onClick={async () => {
                  // Confirmed, and the message says which of the two this is: a
                  // directory here is only removable while it is empty, and the
                  // server refuses the rest rather than emptying it.
                  const ok = await confirm({
                    title: t('files.remove', { name: e.name }),
                    message: t(e.dir ? 'files.removeDirConfirm' : 'files.removeConfirm', {
                      name: e.name,
                    }),
                    confirmLabel: t('action.delete'),
                    tone: 'danger',
                  })
                  if (ok) op.mutate({ op: 'remove', path: join(e.name) })
                }}
              >
                <Icon icon={Trash2} size={13} />
              </button>
            </li>
          ))}
      </ul>
    </QueryState>
  )
}

function Viewer({
  projectId,
  repo,
  path,
  onClose,
}: {
  projectId: string
  repo: Repository
  path: string
  onClose: () => void
}) {
  const t = useT()
  const q = useQuery({
    queryKey: ['repo-file', repo.id, path],
    queryFn: () =>
      api.get<FileContent>(`/repositories/${repo.id}/files/content`, { projectId, path }),
  })

  return (
    <div className="k-files__viewer">
      <header className="k-files__viewhead">
        <span className="k-files__viewname k-mono">{path}</span>
        {q.data && <span className="k-files__size k-mono">{formatBytes(q.data.size)}</span>}
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t('action.close')}
        </Button>
      </header>
      <QueryState pending={q.isPending} error={q.error} onRetry={() => void q.refetch()}>
        {q.data?.binary ? (
          /* Said rather than rendered: a page of replacement characters is not a
             view of a file, and the bytes are what decided this, not the name. */
          <Notice tone="info">{t('files.binary')}</Notice>
        ) : (
          <pre className="k-files__content">{q.data?.content}</pre>
        )}
      </QueryState>
    </div>
  )
}
